import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, CircularProgress, Alert } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { apiFetch } from '../config/api';

interface SessionSummary {
  _id: string;
  modelUsed: string;
  startedAt: string;
  endedAt: string;
  durationSeconds: number;
  summary: {
    avgHR?: number;
    avgSBP?: number;
    avgDBP?: number;
    avgHRV?: number;
    avgRR?: number;
  };
}

const fmt = (iso: string) =>
  new Date(iso).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

const fmtDur = (s: number) => (s < 60 ? `${s}s` : `${Math.round(s / 60)}m`);

const HistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    apiFetch('/api/sessions')
      .then(async (r) => {
        if (!r.ok) throw new Error('Failed to load sessions');
        return r.json() as Promise<SessionSummary[]>;
      })
      .then(setSessions)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const chartData = [...sessions]
    .reverse()
    .slice(-20)
    .map((s, i) => ({
      label: i === 0 ? fmt(s.endedAt) : new Date(s.endedAt).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }),
      SBP: s.summary.avgSBP,
      DBP: s.summary.avgDBP,
      HR: s.summary.avgHR,
    }));

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #0a1628 0%, #0d1f3c 60%, #0a1628 100%)',
      px: { xs: 1.5, md: 3 },
      py: { xs: 1.5, md: 2.5 },
      display: 'flex',
      flexDirection: 'column',
      gap: { xs: 1.5, md: 2 },
    }}>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Button
          startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
          onClick={() => navigate('/')}
          sx={{ color: 'rgba(255,255,255,0.45)', textTransform: 'none', fontSize: '0.82rem', fontWeight: 500, '&:hover': { color: 'white', bgcolor: 'transparent' } }}
        >
          Back
        </Button>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: 'rgba(255,255,255,0.85)', letterSpacing: '4px', textTransform: 'uppercase' }}>
          SeeVita
        </Typography>
        <Box sx={{ minWidth: 60 }} />
      </Box>

      <Typography sx={{ color: 'white', fontSize: { xs: '1.4rem', md: '1.9rem' }, fontWeight: 800 }}>
        Session History
      </Typography>

      {error && <Alert severity="warning" sx={{ borderRadius: '12px', py: 0.5 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress sx={{ color: '#60a5fa' }} />
        </Box>
      ) : sessions.length === 0 ? (
        <Box sx={{ ...cardSx, alignItems: 'center', py: 5 }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.9rem' }}>
            No sessions recorded yet. Complete a monitoring session to see your history.
          </Typography>
        </Box>
      ) : (
        <>
          {chartData.length >= 2 && (
            <Box sx={cardSx}>
              <Typography sx={cardLabelSx}>Trends (last {chartData.length} sessions)</Typography>
              <Box sx={{ height: 220, mt: 1.5 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 4, right: 12, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="label" tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 10 }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 10 }} tickLine={false} axisLine={false} domain={['auto', 'auto']} />
                    <Tooltip
                      contentStyle={{ background: '#0d1f3c', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, fontSize: 12 }}
                      labelStyle={{ color: 'rgba(255,255,255,0.6)' }}
                      itemStyle={{ color: 'white' }}
                    />
                    <Legend wrapperStyle={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }} />
                    <Line type="monotone" dataKey="SBP" stroke="#60a5fa" strokeWidth={2} dot={false} name="SBP mmHg" />
                    <Line type="monotone" dataKey="DBP" stroke="#a78bfa" strokeWidth={2} dot={false} name="DBP mmHg" />
                    <Line type="monotone" dataKey="HR" stroke="#f87171" strokeWidth={2} dot={false} name="HR BPM" />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
            {sessions.map((s) => (
              <Box key={s._id} sx={{ ...cardSx, flexDirection: 'row', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: 160 }}>
                  <Typography sx={{ color: 'white', fontSize: '0.88rem', fontWeight: 700 }}>
                    {fmt(s.endedAt)}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.72rem', mt: 0.3 }}>
                    {s.modelUsed.replace(/\.pth$/i, '').replace(/_/g, ' ')} · {fmtDur(s.durationSeconds)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  {s.summary.avgHR != null && <Chip label="HR" value={s.summary.avgHR} unit="BPM" color="#f87171" />}
                  {s.summary.avgSBP != null && <Chip label="SBP" value={s.summary.avgSBP} unit="mmHg" color="#60a5fa" />}
                  {s.summary.avgDBP != null && <Chip label="DBP" value={s.summary.avgDBP} unit="mmHg" color="#a78bfa" />}
                  {s.summary.avgHRV != null && <Chip label="HRV" value={s.summary.avgHRV} unit="ms" color="#818cf8" />}
                  {s.summary.avgRR != null && <Chip label="RR" value={s.summary.avgRR} unit="/min" color="#2dd4bf" />}
                </Box>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

const Chip: React.FC<{ label: string; value: number; unit: string; color: string }> = ({ label, value, unit, color }) => (
  <Box sx={{ textAlign: 'center', minWidth: 52 }}>
    <Typography sx={{ color, fontSize: '0.95rem', fontWeight: 800, lineHeight: 1.2 }}>{value}</Typography>
    <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.6rem', letterSpacing: '0.5px' }}>{label} {unit}</Typography>
  </Box>
);

const cardSx = {
  background: 'rgba(10, 28, 60, 0.6)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid rgba(255,255,255,0.07)',
  borderRadius: '20px',
  boxShadow: '0 8px 32px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.05)',
  p: { xs: 1.5, md: 2 },
  display: 'flex',
  flexDirection: 'column' as const,
};

const cardLabelSx = {
  fontSize: '0.68rem',
  fontWeight: 700,
  color: 'rgba(255,255,255,0.35)',
  letterSpacing: '2.5px',
  textTransform: 'uppercase' as const,
};

export default HistoryPage;
